import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { Dispatch } from 'redux';
import Authenticator from '../../components/cognito/Authenticator';
import Container from '../../components/Container';
import Layout from '../../components/Layout';
import LeftItem from '../../components/LeftItem';
import Section from '../../components/Section';
import styled from '../../styled-components';
import { AppState } from '../../types/store';
import { acceptApplication, fetchVacancy, newHiringManagerVideo, rejectApplication } from './actions';
import AnswerVideoBox from './components/AnswerVideoBox';
import CandidateAvatar from './components/CandidateAvatar';
import Decision from './components/Decision';
import Documents from './components/Documents';
import HiringManagerVideoBox from './components/HiringManagerVideoBox';
import QuestionsBox from './components/QuestionsBox';
import { getCurrentVacancy } from './reducer';
import { Application, Vacancy } from './types';

interface HiringManagerStateProps {
    vacancy: Vacancy | undefined;
}

interface HiringManagerDispatchProps {
    fetchVacancy: (vacancyId: string) => void;
    onAccept: (application: Application) => void;
    onReject: (application: Application) => void;
    onNewHiringManagerVideo: (vacancy: Vacancy, videoReference: string) => void;
}

interface HiringManagerRouterParams {
    vacancyId: string;
}

interface HiringManagerProps
    extends HiringManagerStateProps,
        HiringManagerDispatchProps,
        RouteComponentProps<HiringManagerRouterParams> {}

interface HiringManagerState {
    selectedApplicationId: string | undefined;
    selectedQuestionKey: string | undefined;
    signedIn?: boolean;
}

const CandidateAvatars = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 5rem;
`;

class HiringManager extends React.Component<HiringManagerProps, HiringManagerState> {
    constructor(props: HiringManagerProps) {
        super(props);
        this.handleAvatarClick = this.handleAvatarClick.bind(this);
        this.handleQuestionClick = this.handleQuestionClick.bind(this);
        this.handleVideoClicked = this.handleVideoClicked.bind(this);
        this.handleNewHiringManagerVideo = this.handleNewHiringManagerVideo.bind(this);
        this.loginComplete = this.loginComplete.bind(this);
        this.handleAuthStateChange = this.handleAuthStateChange.bind(this);
        this.state = { selectedApplicationId: undefined, selectedQuestionKey: undefined };
    }

    public loginComplete() {
        this.props.fetchVacancy(this.props.match.params.vacancyId);
    }

    private handleAvatarClick(application: Application) {
        const selectedQuestionKey = this.state.selectedQuestionKey
            ? this.state.selectedQuestionKey
            : application.answers[0].key;
        this.setState({ selectedApplicationId: application.id, selectedQuestionKey });
    }

    private handleQuestionClick(questionKey: string) {
        this.setState({ selectedQuestionKey: questionKey });
    }

    private getVideoId(application: Application): string | undefined {
        const videoIds = application.answers.map(a => a.videoId).filter(vid => vid !== undefined);
        return videoIds.length > 0 ? videoIds[0] : undefined;
    }

    private handleVideoClicked() {
        this.setState({ selectedApplicationId: undefined });
    }

    private handleNewHiringManagerVideo(videoReference: string) {
        this.props.onNewHiringManagerVideo(this.props.vacancy!, videoReference);
    }

    private handleAuthStateChange(state) {
        const signedIn = state === 'signedIn';
        if (signedIn) {
            this.loginComplete();
        }
        this.setState({ signedIn });
    }

    public render() {
        const selectedApplication: Application | undefined = this.props.vacancy
            ? this.state.selectedApplicationId
                ? this.props.vacancy.applications.find(a => a.id === this.state.selectedApplicationId)
                : undefined
            : undefined;
        return (
            <div>
                <Authenticator onStateChange={this.handleAuthStateChange} />
                {this.state.signedIn &&
                    this.props.vacancy && (
                        <Section>
                            <Container>
                                <h1>V-Sponses to vacancy {this.props.vacancy.name}</h1>
                                <CandidateAvatars>
                                    <CandidateAvatar
                                        key="video"
                                        videoId={undefined}
                                        firstLine="Your"
                                        secondLine="Video"
                                        selected={this.state.selectedApplicationId === undefined}
                                        onClick={this.handleVideoClicked}
                                    />
                                    {this.props.vacancy.applications.map(a => (
                                        <CandidateAvatar
                                            key={a.id}
                                            videoId={this.getVideoId(a)}
                                            firstLine={a.firstName}
                                            secondLine={a.lastName}
                                            selected={
                                                this.state.selectedApplicationId
                                                    ? this.state.selectedApplicationId === a.id
                                                    : false
                                            }
                                            onClick={() => this.handleAvatarClick(a)}
                                        />
                                    ))}
                                </CandidateAvatars>
                                {selectedApplication && (
                                    <Layout>
                                        <Decision
                                            onAccept={this.props.onAccept}
                                            onReject={this.props.onReject}
                                            application={selectedApplication}
                                        />
                                    </Layout>
                                )}
                                {selectedApplication === undefined && (
                                    <Layout>
                                        <LeftItem>
                                            <h2>Your video</h2>
                                            <HiringManagerVideoBox
                                                vacancyId={this.props.vacancy.id}
                                                videoReference={this.props.vacancy.hiringManagerVideoReference}
                                                onNewVideo={this.handleNewHiringManagerVideo}
                                            />
                                        </LeftItem>
                                    </Layout>
                                )}
                                {selectedApplication && (
                                    <Layout>
                                        <LeftItem>
                                            <div>
                                                <h2>Questions</h2>
                                                <QuestionsBox
                                                    application={selectedApplication}
                                                    selectedQuestionKey={this.state.selectedQuestionKey}
                                                    onClick={this.handleQuestionClick}
                                                />
                                            </div>
                                        </LeftItem>
                                        {this.state.selectedQuestionKey && (
                                            <AnswerVideoBox
                                                answer={getAnswerOfSelectedQuestion(this.state, selectedApplication)}
                                            />
                                        )}
                                    </Layout>
                                )}
                                {selectedApplication &&
                                    selectedApplication.documents.length > 0 && (
                                        <div>
                                            <h2>Documents</h2>
                                            {
                                                <p>
                                                    {selectedApplication.firstName} {selectedApplication.lastName} has
                                                    provided you with the following documents:
                                                </p>
                                            }
                                            <Documents documents={selectedApplication.documents} />
                                        </div>
                                    )}
                            </Container>
                        </Section>
                    )}
            </div>
        );
    }
}

const getAnswerOfSelectedQuestion = (state: HiringManagerState, selectedApplication: Application) => {
    return selectedApplication ? selectedApplication.answers.find(a => a.key === state.selectedQuestionKey) : undefined;
};

export const mapStateToProps = (state: AppState, ownProps): HiringManagerStateProps => {
    return {
        vacancy: getCurrentVacancy(state)
    };
};

export const mapDispatchToProps = (dispatch: Dispatch<any>): HiringManagerDispatchProps => {
    return {
        fetchVacancy: (vacancyId: string) => dispatch(fetchVacancy(vacancyId)),
        onAccept: (application: Application) => dispatch(acceptApplication(application)),
        onReject: (application: Application) => dispatch(rejectApplication(application)),
        onNewHiringManagerVideo: (vacancy: Vacancy, videoReference: string) =>
            dispatch(newHiringManagerVideo(vacancy, videoReference))
    };
};

export default connect<HiringManagerStateProps, HiringManagerDispatchProps, {}>(
    mapStateToProps,
    mapDispatchToProps
)(HiringManager);



// WEBPACK FOOTER //
// ./src/scenes/hiring-manager/index.tsx